import {
  Calendar,
  Card,
  Select,
  Space,
  Tag,
  Tooltip,
  Table,
  Modal,
} from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import getUserFromToken from "../../utils/getUserFromToken";
import { loadMonthlyAttendance } from "../../redux/rtk/features/attendance/attendanceSlice";
import { useParams } from "react-router-dom";

// Add the isBetween plugin to dayjs
dayjs.extend(isBetween);

const AttendanceCalendar = () => {
  let { id } = useParams("id");
  // console.log("userId", id);
  const dispatch = useDispatch();
  const EmployeeId = getUserFromToken();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Get attendance data from Redux
  const { attendanceData: attendanceData = [], loading = false } = useSelector(
    (state) => state?.attendance || {}
  );

  // console.log("attendanceData", attendanceData);
  
  if (id === "{user}") {
    id = EmployeeId;
  } else {
    id = parseInt(id);
  }

  // Initial data loading
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(
          loadMonthlyAttendance({
            Month: selectedDate.month() + 1,
            Year: selectedDate.year(),
            EmployeeId: id,
          })
        );
      } catch (error) {
        // console.error("Error loading calendar data:", error);
        toast.error("Failed to load calendar data");
      }
    };
    loadData();
  }, [dispatch, id, selectedDate]);

  // Handlers for month and year selection
  const handleMonthChange = useCallback((value) => {
    setSelectedDate((prev) => prev.month(value));
  }, []);

  const handleYearChange = useCallback((value) => {
    setSelectedDate((prev) => prev.year(value));
  }, []);

  // Memoized options for dropdowns
  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i,
        label: dayjs().month(i).format("MMMM"),
      })),
    []
  );

  const yearOptions = useMemo(
    () =>
      Array.from({ length: 5 }, (_, i) => ({
        value: dayjs().year() - i,
        label: `${dayjs().year() - i}`,
      })),
    []
  );

  // Define status colors based on your API data
  const statusColors = {
    Present: "#52c41a", // Green
    Absent: "#ff4d4f", // Red
    PublicHoliday: "#fa8c16", // Orange
    Leave: "#eb2f96", // Pink
    ComboOff: "#722ed1", // Purple
    HalfDay: "#fadb14", // Yellow
    ShortLeave: "#13c2c2", // Cyan
    NonWorking: "#d9d9d9", // Grey
  };

  const getAttendanceStatus = (record, date) => {
    // console.log("record", record);

    // Don't show future dates
    const isFutureDate = date.isAfter(dayjs(), "day");
    if (isFutureDate) {
      return null; // Don't render anything for future dates
    }

    if (!record) {
      return {
        status: "No Data",
        color: statusColors.NonWorking,
        details: "No data available",
        displayItems: []
      };
    }

    let displayItems = [];
    let mainStatus = "";
    let backgroundColor = statusColors.NonWorking;

    // Public Holiday
    if (record.IsPublicHoliday) {
      mainStatus = "Public Holiday";
      backgroundColor = statusColors.PublicHoliday;
      displayItems.push({
        type: "holiday", 
        text: record.HolidayName || "Holiday",
        color: "#fa8c16"
      });
    }
    // Leave
    else if (record.IsOnLeave) {
      mainStatus = "Leave";
      backgroundColor = statusColors.Leave;
      displayItems.push({
        type: "leave", 
        text: record.LeaveTypeName || "Leave",
        color: "#eb2f96"
      });
    }
    // Combo Off
    else if (record.IsComboOff) {
      mainStatus = "Combo Off";
      backgroundColor = statusColors.ComboOff;
      displayItems.push({
        type: "combo", 
        text: "Combo Off",
        color: "#722ed1"
      });
    }
    // Present
    else if (record.IsPresent) {
      mainStatus = "Present";
      backgroundColor = statusColors.Present;
      displayItems.push({
        type: "present", 
        text: "Present",
        color: "#52c41a"
      });
    }
    // Absent (Working Day but not present)
    else if (record.IsWorkingDay && !record.IsPresent) {
      mainStatus = "Absent";
      backgroundColor = statusColors.Absent;
      displayItems.push({
        type: "absent", 
        text: "Absent",
        color: "#ff4d4f"
      });
    }
    // Non-working day
    else {
      mainStatus = "Non-Working";
      backgroundColor = statusColors.NonWorking;
      displayItems.push({
        type: "nonworking", 
        text: "Non-Working",
        color: "#d9d9d9"
      });
    }

    // Add partial leave indicators
    const partialLeaves = [];
    if (record.IsHalfDayOnStart) partialLeaves.push("Half Day (Start)");
    if (record.IsHalfDayOnMid) partialLeaves.push("Half Day (Mid)");
    if (record.IsHalfDayOnEnd) partialLeaves.push("Half Day (End)");
    
    if (partialLeaves.length > 0) {
      displayItems.push({
        type: "partial", 
        text: partialLeaves.join(", "),
        color: "#fadb14"
      });
    }

    const shortLeaves = [];
    if (record.IsShortLeaveOnStart) shortLeaves.push("Short Leave (Start)");
    if (record.IsShortLeaveOnMid) shortLeaves.push("Short Leave (Mid)");
    if (record.IsShortLeaveOnEnd) shortLeaves.push("Short Leave (End)");
    
    if (shortLeaves.length > 0) {
      displayItems.push({
        type: "short", 
        text: shortLeaves.join(", "),
        color: "#13c2c2"
      });
    }

    return {
      status: mainStatus,
      color: backgroundColor,
      details: mainStatus,
      displayItems: displayItems,
      record: record
    };
  };

  const handleDateClick = (date, record) => {
    if (record) {
      setSelectedDayData({
        date: date.format("YYYY-MM-DD"),
        ...record,
      });
      setShowDetailModal(true);
    }
  };

  // Memoized cell renderer for calendar
  const dateCellRender = useCallback(
    (date) => {
      // Only render cells for the current month and up to today
      if (date.month() !== selectedDate.month()) return null;
      
      // Don't show future dates content, but show the date number
      const isFutureDate = date.isAfter(dayjs(), "day");

      // Format the date to match API date format for comparison
      const formattedDate = date.format("YYYY-MM-DD");

      // Find matching attendance record
      const record = Array.isArray(attendanceData)
        ? attendanceData.find(
            (item) =>
              dayjs(item.AttendanceDate).format("YYYY-MM-DD") === formattedDate
          )
        : null;

      // Get status information
      const statusInfo = !isFutureDate ? getAttendanceStatus(record, date) : null;

      // Check if the date is the current date
      const isCurrentDate = date.isSame(dayjs(), "day");

      const hoverContent = record ? (
        <div style={{ maxWidth: 360, color: "#000" }}>
          <div><strong>{date.format("DD MMM YYYY")}</strong></div>
          {record.PunchInLocation ? (
            <div style={{ marginTop: 4 }}>
              <strong>Punch In Location:</strong> {record.PunchInLocation}
            </div>
          ) : null}
          {record.PunchOutLocation ? (
            <div>
              <strong>Punch Out Location:</strong> {record.PunchOutLocation}
            </div>
          ) : null}
        </div>
      ) : null;

      const dayCell = (
        <div
          style={{
            width: "100%",
            height: "100%",
            minHeight: "100px",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            color: "#000",
            fontSize: "13px", // Increased from 11px
            cursor: record ? "pointer" : "default",
            padding: "6px 4px 4px 4px",
            position: "relative",
            boxSizing: "border-box",
            overflow: "hidden"
          }}
          // onClick={() => record && handleDateClick(date, record)}
        >
          {/* Date number */}
          <div style={{ 
            fontWeight: "bold", 
            fontSize: "18px", // Increased from 16px
            marginBottom: "3px",
            color: isFutureDate ? "#ccc" : "#000",
            lineHeight: "1"
          }}>
            {date.date()}
          </div>
          
          {/* Attendance information - only show for non-future dates */}
          {!isFutureDate && statusInfo && statusInfo.displayItems && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "1px",
              fontSize: "13px", // Increased from 11px
              lineHeight: "1.2",
              flex: 1
            }}>
              {/* Main status */}
              {statusInfo.displayItems.map((item, index) => {
                if (index === 0) {
                  return (
                    <div
                      key={index}
                      style={{
                        color: item.color,
                        fontWeight: "500",
                        fontSize: "13px" // Increased from 11px
                      }}
                    >
                      • {item.text}
                    </div>
                  );
                }
                return (
                  <div
                    key={index}
                    style={{
                      color: item.color,
                      fontWeight: "normal",
                      fontSize: "12px" // Increased from 10px
                    }}
                  >
                    • {item.text}
                  </div>
                );
              })}
              
              {/* Show detailed time info for present days */}
              {record && record.IsPresent && (
                <>
                  {record.PunchInTime && (
                    <div style={{ 
                      color: "#666", 
                      fontSize: "12px", // Increased from 10px
                      lineHeight: "1.2"
                    }}>
                      • In: {dayjs(record.PunchInTime).format("HH:mm")}
                    </div>
                  )}
                  {record.PunchOutTime && (
                    <div style={{ 
                      color: "#666", 
                      fontSize: "12px", // Increased from 10px
                      lineHeight: "1.2"
                    }}>
                      • Out: {dayjs(record.PunchOutTime).format("HH:mm")}
                    </div>
                  )}
                  {record.WorkingHours && (
                    <div style={{ 
                      color: "#666", 
                      fontSize: "12px", // Increased from 10px
                      lineHeight: "1.2"
                    }}>
                      • {record.WorkingHours}h total
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Current date indicator */}
          {isCurrentDate && (
            <div
              style={{
                position: "absolute",
                top: "2px",
                right: "4px",
                backgroundColor: "#1890ff",
                color: "#fff",
                fontSize: "10px", // Increased from 8px
                padding: "1px 4px",
                borderRadius: "2px",
                fontWeight: "bold"
              }}
            >
              Today
            </div>
          )}
        </div>
      );

      if (!hoverContent) {
        return dayCell;
      }

      return (
        <Tooltip
          title={hoverContent}
          placement="topLeft"
          color="#ffffff"
          overlayInnerStyle={{ color: "#000000" }}
        >
          {dayCell}
        </Tooltip>
      );
    },
    [attendanceData, selectedDate]
  );

  // Detail modal columns
  const detailColumns = [
    {
      title: "Attribute",
      dataIndex: "attribute",
      key: "attribute",
      width: "40%",
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      width: "60%",
    },
  ];

  // Prepare data for detail modal
  const detailData = useMemo(() => {
    if (!selectedDayData) return [];

    return [
      {
        key: "1",
        attribute: "Date",
        value: dayjs(selectedDayData.date).format("DD MMM YYYY (dddd)"),
      },
      {
        key: "2",
        attribute: "Week Day",
        value: selectedDayData.WeekDayName || "N/A",
      },
      {
        key: "3",
        attribute: "Week Number",
        value: selectedDayData.WeekNumber || "N/A",
      },
      {
        key: "4",
        attribute: "Working Day",
        value: selectedDayData.IsWorkingDay ? "Yes" : "No",
      },
      {
        key: "5",
        attribute: "Present",
        value: selectedDayData.IsPresent ? "Yes" : "No",
      },
      {
        key: "6",
        attribute: "Public Holiday",
        value: selectedDayData.IsPublicHoliday ? `Yes${selectedDayData.HolidayName ? ` (${selectedDayData.HolidayName})` : ''}` : "No",
      },
      {
        key: "7",
        attribute: "On Leave",
        value: selectedDayData.IsOnLeave ? `Yes${selectedDayData.LeaveTypeName ? ` (${selectedDayData.LeaveTypeName})` : ''}` : "No",
      },
      {
        key: "8",
        attribute: "Combo Off",
        value: selectedDayData.IsComboOff ? "Yes" : "No",
      },
      {
        key: "9",
        attribute: "Punch In",
        value: selectedDayData.PunchInTime
          ? dayjs(selectedDayData.PunchInTime).format("hh:mm A")
          : "N/A",
      },
      {
        key: "10",
        attribute: "Punch Out",
        value: selectedDayData.PunchOutTime
          ? dayjs(selectedDayData.PunchOutTime).format("hh:mm A")
          : "N/A",
      },
      {
        key: "11",
        attribute: "Working Hours",
        value: selectedDayData.WorkingHours || "N/A",
      },
      {
        key: "12",
        attribute: "Overtime Hours",
        value: selectedDayData.OvertimeHours || "N/A",
      },
      {
        key: "13",
        attribute: "Punch In Remark",
        value: selectedDayData.PunchInRemark || "N/A",
      },
      {
        key: "14",
        attribute: "Punch Out Remark",
        value: selectedDayData.PunchOutRemark || "N/A",
      },
      {
        key: "15",
        attribute: "Half Day (Start)",
        value: selectedDayData.IsHalfDayOnStart ? "Yes" : "No",
      },
      {
        key: "16",
        attribute: "Half Day (Mid)",
        value: selectedDayData.IsHalfDayOnMid ? "Yes" : "No",
      },
      {
        key: "17",
        attribute: "Half Day (End)",
        value: selectedDayData.IsHalfDayOnEnd ? "Yes" : "No",
      },
      {
        key: "18",
        attribute: "Short Leave (Start)",
        value: selectedDayData.IsShortLeaveOnStart ? "Yes" : "No",
      },
      {
        key: "19",
        attribute: "Short Leave (Mid)",
        value: selectedDayData.IsShortLeaveOnMid ? "Yes" : "No",
      },
      {
        key: "20",
        attribute: "Short Leave (End)",
        value: selectedDayData.IsShortLeaveOnEnd ? "Yes" : "No",
      },
    ];
  }, [selectedDayData]);

  return (
    <div className="attendance-calendar-page-wrapper">
      <Card className="w-full attendance-calendar-card">
        <style>
        {`
          .ant-picker-cell-inner::before {
            display: none !important;
          }
          .ant-picker-cell-inner {
            color: transparent !important;
            position: relative;
            width: 100% !important;
            height: 100% !important;
            padding: 0 !important;
          }
          .ant-picker-cell {
            padding: 2px !important;
            height: 120px !important;
            vertical-align: top !important;
          }
          .ant-picker-calendar .ant-picker-panel .ant-picker-body {
            height: auto !important;
          }
          .ant-picker-date-panel .ant-picker-body table tbody tr {
            height: 120px !important;
          }
          .ant-picker-calendar .ant-picker-cell-today .ant-picker-calendar-date {
            background: transparent !important;
          }
          .ant-picker-calendar .ant-picker-content th {
            font-weight: bold;
            text-align: center;
            padding: 8px 4px;
            background-color: #fafafa;
            border-bottom: 1px solid #f0f0f0;
            font-size: 14px; /* Increased from default for weekday headers */
          }
          .ant-picker-calendar .ant-picker-content table {
            border-collapse: separate;
            border-spacing: 2px;
          }
          .ant-picker-calendar .ant-picker-cell {
            border: none !important;
          }
        `}
      </style>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space>
          <Select
            value={selectedDate.month()}
            onChange={handleMonthChange}
            options={monthOptions}
            style={{ width: 120, fontSize: "14px" }} /* Increased font size for dropdown */
          />
          <Select
            value={selectedDate.year()}
            onChange={handleYearChange}
            options={yearOptions}
            style={{ width: 120, fontSize: "14px" }} /* Increased font size for dropdown */
          />
        </Space>

        {loading ? (
          <div style={{ padding: "20px", textAlign: "center", fontSize: "16px" /* Increased font size */ }}>
            Loading attendance data...
          </div>
        ) : (
          <>
            <Calendar
              value={selectedDate}
              dateCellRender={dateCellRender}
              fullscreen={false}
              headerRender={() => null}
              onChange={(newDate) => setSelectedDate(newDate)}
            />

            {/* Detail Modal */}
            {showDetailModal && (
              <Modal
                title={`Attendance Details - ${dayjs(
                  selectedDayData?.date
                ).format("DD MMM YYYY")}`}
                open={showDetailModal}
                onCancel={() => setShowDetailModal(false)}
                footer={null}
                width={600}
              >
                <Table
                  columns={detailColumns}
                  dataSource={detailData}
                  pagination={false}
                  size="small"
                  bordered
                  style={{ fontSize: "14px" }} /* Increased font size for table */
                />
              </Modal>
            )}
          </>
        )}
      </Space>
      </Card>
    </div>
  );
};

export default AttendanceCalendar;